import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { KeyIcon, EyeIcon, EyeSlashIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, SimpleNotificationContainer } from '../../components/ui';
import { useUIStore } from '../../store/uiStore';
import { authService } from '../../services/api';

const forgotPasswordSchema = z.object({
  email: z.string()
    .min(1, 'El email es requerido')
    .email('Ingresa un email válido'),
});

const resetPasswordSchema = z.object({
  email: z.string()
    .min(1, 'El email es requerido')
    .email('Ingresa un email válido'),
  recovery_code: z.string()
    .min(6, 'El código debe tener 6 dígitos')
    .max(6, 'El código debe tener 6 dígitos'),
  new_password: z.string()
    .min(6, 'La nueva contraseña debe tener al menos 6 caracteres'),
  confirm_password: z.string()
    .min(1, 'Confirma tu nueva contraseña'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Las contraseñas no coinciden",
  path: ["confirm_password"],
});

export const ForgotPasswordPage = () => {
  const { addNotification } = useUIStore();
  const [step, setStep] = useState('forgot'); // 'forgot' or 'reset'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  const forgotForm = useForm({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const resetForm = useForm({
    resolver: zodResolver(resetPasswordSchema),
  });

  const onForgotSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const response = await authService.forgotPassword(data.email);
      
      if (response.success) {
        setUserEmail(data.email);
        setStep('reset');
        // Establecer el email en el formulario de reset
        resetForm.setValue('email', data.email);
        addNotification({
          type: 'success',
          title: 'Código enviado',
          message: 'Revisa tu email para el código de recuperación',
        });
      } else {
        addNotification({
          type: 'error',
          title: 'Error',
          message: response.message || 'Ha ocurrido un error inesperado',
        });
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Ha ocurrido un error inesperado',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResetSubmit = async (data) => {
    console.log('onResetSubmit called with data:', data);
    setIsSubmitting(true);
    
    try {
      console.log('Calling authService.resetPassword...');
      const response = await authService.resetPassword(data);
      console.log('Response received:', response);
      
      if (response.success) {
        addNotification({
          type: 'success',
          title: 'Contraseña restablecida',
          message: 'Tu contraseña ha sido cambiada exitosamente. Puedes iniciar sesión ahora.',
        });
        // Redirigir al login después de 2 segundos
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        addNotification({
          type: 'error',
          title: 'Error al restablecer',
          message: response.message || 'Ha ocurrido un error inesperado',
        });
      }
    } catch (error) {
      console.error('Reset password error:', error);
      addNotification({
        type: 'error',
        title: 'Error al restablecer',
        message: error.message || 'Ha ocurrido un error inesperado',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    setStep('forgot');
    setUserEmail('');
    resetForm.reset();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Title */}
        <div className="text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <KeyIcon className="h-8 w-8 text-primary-600" />
          </div>
          <div className="mt-6">
            <h1 className="text-4xl font-bold text-gray-900">
              DICOMITY
            </h1>
            <h2 className="text-2xl font-semibold text-gray-700 mt-1">
              License Manager
            </h2>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {step === 'forgot' ? 'Recupera tu contraseña' : 'Ingresa el código de recuperación'}
          </p>
        </div>

        {/* Forgot Password Form */}
        {step === 'forgot' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-center">¿Olvidaste tu contraseña?</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={forgotForm.handleSubmit(onForgotSubmit)} className="space-y-6">
                <Input
                  label="Email"
                  type="email"
                  placeholder="Ingresa tu email"
                  error={forgotForm.formState.errors.email?.message}
                  {...forgotForm.register('email')}
                />

                <Button
                  type="submit"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Enviando código...' : 'Enviar código de recuperación'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <a
                  href="/login"
                  className="text-sm text-primary-600 hover:text-primary-500"
                >
                  ← Volver al inicio de sesión
                </a>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reset Password Form */}
        {step === 'reset' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-center">Restablecer contraseña</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={(e) => {
                console.log('Form submit event triggered');
                resetForm.handleSubmit(onResetSubmit)(e);
              }} className="space-y-6">
                <Input
                  label="Email"
                  type="email"
                  value={userEmail}
                  disabled
                  className="bg-gray-50"
                  {...resetForm.register('email')}
                />

                <Input
                  label="Código de recuperación"
                  type="text"
                  placeholder="Ingresa el código de 6 dígitos"
                  maxLength={6}
                  error={resetForm.formState.errors.recovery_code?.message}
                  {...resetForm.register('recovery_code')}
                />

                <Input
                  label="Nueva Contraseña"
                  type={showNewPassword ? "text" : "password"}
                  placeholder="Ingresa tu nueva contraseña"
                  error={resetForm.formState.errors.new_password?.message}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title={showNewPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showNewPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  }
                  {...resetForm.register('new_password')}
                />

                <Input
                  label="Confirmar Nueva Contraseña"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirma tu nueva contraseña"
                  error={resetForm.formState.errors.confirm_password?.message}
                  rightIcon={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title={showConfirmPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    >
                      {showConfirmPassword ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                  }
                  {...resetForm.register('confirm_password')}
                />

                <Button
                  type="submit"
                  className="w-full"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Restableciendo contraseña...' : 'Restablecer contraseña'}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={goBack}
                  className="text-sm text-primary-600 hover:text-primary-500 flex items-center justify-center"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-1" />
                  Volver a solicitar código
                </button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 DICOMITY License Manager. Todos los derechos reservados.
          </p>
        </div>
      </div>
      
      {/* Global Notifications */}
      <SimpleNotificationContainer />
    </div>
  );
};
